import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ModuleShowcase from '../components/ModuleShowcase';
import { moduleBySlug } from '../data/modules';
import { PILOT, platformStart, appKit } from '../data/plans';
import PageWorld from '../components/site/PageWorld';
import { MODULE } from '../data/worlds';
import { ModuleFilm, moduleFilmsEnabled, MODULE_FILM, PageFilm, pageFilmsEnabled } from '../components/PageFilm';

// /platform/:slug — product page for one name or module. Copy lives in
// data/modules.js so the card, the nav, and this page cannot drift.
// Next follows the public nav, not the array: design, runtime, the agent,
// then the capabilities. Dev Console belongs to App Kit.

const PLATFORM_FLOW = ['nqube', 'training-center', 'agentos', 'agent-io', 'nventr-agent', 'athena', 'listen', 'reachout', 'devops'];

function nextStep(slug) {
  if (slug === 'dev-console') return { to: '/app-kit#foundation', name: 'The foundation' };
  const i = PLATFORM_FLOW.indexOf(slug);
  if (i === -1 || i === PLATFORM_FLOW.length - 1) return { to: '/#proof', name: 'How it works' };
  const n = moduleBySlug(PLATFORM_FLOW[i + 1]);
  return { to: `/platform/${n.slug}`, name: n.name };
}

export default function ModulePage() {
  const { slug } = useParams();
  const mod = moduleBySlug(slug);

  if (!mod) return <Navigate to="/platform" replace />;

  const next = nextStep(mod.slug);
  const related = (mod.see || [])
    .map((s) => moduleBySlug(s))
    .filter(Boolean);
  const kitPage = mod.slug === 'dev-console';
  const buy = kitPage ? appKit : platformStart;

  const film = moduleFilmsEnabled() && MODULE_FILM[mod.slug];
  // A module with its own narrated page film (the Training Center) shows
  // it full width under the hero, like /platform and /pricing do.
  const pageFilm = mod.film && pageFilmsEnabled() ? mod.film : null;
  return (
    <PageWorld world={MODULE}>
    <div className="cs-store cs-land">
      <header className="cs-store-hero cs-mod-hero cs-mod-hero-stage">
        <div className="cs-mod-hero-copy">
          <p className="cs-mod-kicker">
            {kitPage ? <Link to="/app-kit">App Kit</Link> : <Link to="/platform">Platform</Link>}
            {' / '}
            {kitPage ? mod.name : (mod.kicker || 'module')}
          </p>
          <h1>{mod.name}</h1>
          <p>{mod.tagline}</p>
          <div className="cs-land-cta-row">
            <Link to={buy.href} className="cs-btn cs-btn-primary">
              {buy.cta}
            </Link>
            {kitPage ? (
              <Link to="/app-kit" className="cs-btn cs-btn-outline">
                How the instance works
              </Link>
            ) : (
              <a href={PILOT.href} className="cs-btn cs-btn-outline">
                {PILOT.label}
              </a>
            )}
          </div>
        </div>
        {!pageFilm && (
          <p className="cs-land-micro" style={{ marginTop: 18 }}>
            {film ? 'The real console, from our own demo and team organizations.' : (mod.screenNote || 'Product screens recreated with synthetic data.')}
          </p>
        )}
        {!pageFilm && (film ? <ModuleFilm slug={mod.slug} alt={mod.tagline} /> : <ModuleShowcase slug={mod.slug} />)}
      </header>

      {pageFilm && (
        <PageFilm base={pageFilm.base} prefix={pageFilm.prefix} title={pageFilm.title} summary={pageFilm.summary} chapters={pageFilm.chapters || []} />
      )}

      <section className="cs-store-section">
        <h2>What it is</h2>
        {(mod.story || [mod.overview]).map((p) => (
          <p className="cs-mod-prose" key={p.slice(0, 48)}>{p}</p>
        ))}
      </section>

      {mod.notThis && (
        <section className="cs-store-section">
          <h2>What it is not</h2>
          <div className="cs-sw-card cs-mod-callout">
            <p>{mod.notThis}</p>
          </div>
        </section>
      )}

      <section className="cs-store-section">
        <div className="cs-mod-split">
          <div className="cs-sw-card">
            <h3>Who it is for</h3>
            <p>{mod.audience}</p>
          </div>
          <div className="cs-sw-card">
            <h3>Where you get it</h3>
            <p>{mod.included}</p>
            <Link to="/pricing" className="cs-land-textlink">Pricing →</Link>
          </div>
        </div>
      </section>

      {mod.scene && (
        <section className="cs-store-section">
          <h2>{mod.sceneTitle || 'A worked example'}</h2>
          <p className="cs-land-micro" style={{ marginTop: 0 }}>Synthetic example. Not a customer.</p>
          <div className="cs-sw-card cs-mod-scene">
            <p>{mod.scene}</p>
          </div>
        </section>
      )}

      {mod.outcomes && (
        <section className="cs-store-section">
          <h2>What changes when you have it</h2>
          <div className="cs-land-steps">
            {mod.outcomes.map((o) => (
              <div key={o.name} className="cs-sw-card cs-land-step">
                <h3>{o.name}</h3>
                <p>{o.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="cs-store-section">
        <h2>Inside {mod.name}</h2>
        {mod.features && (
          <ul className="cs-land-edge-bullets ms-features">
            {mod.features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
      </section>

      {mod.walkthrough && (
        <section className="cs-store-section" id="coordinator">
          <h2>{mod.walkthroughTitle || 'Walkthrough'}</h2>
          <div className="cs-land-steps">
            {mod.walkthrough.map((s, i) => (
              <div key={s.name} className="cs-sw-card cs-land-step">
                <div className="cs-land-step-n">{i + 1}</div>
                <h3>{s.name}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="cs-store-section">
        <h2>How it works</h2>
        <div className="cs-land-steps">
          {mod.steps.map((s, i) => (
            <div key={s.name} className="cs-sw-card cs-land-step">
              <div className="cs-land-step-n">{i + 1}</div>
              <h3>{s.name}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
        <div className="cs-land-metered">
          <strong>{mod.name}.</strong> {mod.tiein}
        </div>
      </section>

      {mod.explainer && (
        <section className="cs-store-section" id={mod.explainer.id}>
          <h2>{mod.explainer.title}</h2>
          {mod.explainer.body.map((p) => <p key={p.slice(0, 48)} className="cs-mod-prose">{p}</p>)}
        </section>
      )}

      {related.length > 0 && (
        <section className="cs-store-section">
          <h2>The names next to it</h2>
          <div className="cs-mod-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/platform/${r.slug}`} className="cs-sw-card cs-mod-related-card">
                <span className="cs-mod-kicker">{r.kicker}</span>
                <h3>{r.name}</h3>
                <p>{r.card}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="cs-land-band">
        <h2>{kitPage ? 'Build it on an instance' : 'See it on your workflow'}</h2>
        <p>
          {kitPage
            ? 'App Kit is the instance — large files, heavy jobs, health checks on every deploy, updates that stay out of your layer. Dev Console is how you change that layer.'
            : 'Platform Start is the self-serve canvas. A first pipeline we stand up with you is delivery, priced apart from the subscription.'}
        </p>
        <div className="cs-land-cta-row cs-land-cta-center">
          <Link to={buy.href} className="cs-btn cs-btn-primary">
            {buy.cta}
          </Link>
          {kitPage ? (
            <Link to="/app-kit" className="cs-btn cs-btn-outline">
              How the instance works
            </Link>
          ) : (
            <a href={PILOT.href} className="cs-btn cs-btn-outline">
              {PILOT.label}
            </a>
          )}
          <Link to={next.to} className="cs-btn cs-btn-outline">
            Next: {next.name} →
          </Link>
        </div>
      </section>
    </div>
    </PageWorld>
  );
}
